import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion'
import { first, nth } from 'lodash'
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { localKeyMap } from '~/common/utils/keymap'
import { ActiveUser } from '~/components/settings/types'
import { MenuItem } from '~/components/ui/atoms/menu-item/MenuItem'
import { Popover } from '~/components/ui/atoms/popover/Popover'
import { UserAvatar } from '../../users/UserAvatar'

export interface MentionListProps extends SuggestionProps {
  open: boolean
}

interface MentionListActions {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean
}

/**
 * Renders the list of organization members for comment mentions
 */
export const MentionList = forwardRef<MentionListActions, MentionListProps>(
  ({ clientRect, command, items, open }, ref) => {
    const { t } = useTypedTranslation()
    const [highlightedOption, setHighlightedOption] = useState<ActiveUser | null>(null)

    const referenceEl = useMemo(() => {
      if (clientRect) {
        return {
          getBoundingClientRect: () => {
            const rect = clientRect()
            return rect ?? new DOMRect()
          },
          nodeType: 1 as const, // Ensure nodeType is a valid property for PopoverVirtualElement
        }
      }
      return null
    }, [clientRect])

    const selectUser = (user: ActiveUser) => {
      command({
        id: user.id,
        label: `${user.firstName} ${user.lastName}`,
      })
    }

    // When the down arrow is hit, highlight the next option in the list
    const handleDownArrow = () => {
      if (!highlightedOption) {
        setHighlightedOption(first(items) ?? null)
        return
      }

      const currentIndex = items.findIndex((option) => option.id === highlightedOption.id)
      if (currentIndex === items.length - 1) {
        return
      }
      const newHighlightedOption = nth(items, currentIndex + 1) ?? null
      setHighlightedOption(newHighlightedOption)
    }

    // When the up arrow is hit, highlight the previous option in the list
    const handleUpArrow = () => {
      if (!highlightedOption) {
        return
      }

      const currentIndex = items.findIndex((option) => option.id === highlightedOption.id)
      if (currentIndex === 0) {
        setHighlightedOption(null)
        return
      }

      const newHighlightedOption = nth(items, currentIndex - 1) ?? null
      setHighlightedOption(newHighlightedOption)
    }

    // When enter is hit, select the highlighted option
    const handleEnter = () => {
      if (!highlightedOption) {
        return false
      }

      selectUser(highlightedOption)
      return true
    }

    /**
     * Handle keyboard shortcuts for the options list
     */
    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: SuggestionKeyDownProps) => {
        if (event.key === localKeyMap.DOWN_ARROW) {
          handleDownArrow()
          return false
        } else if (event.key === localKeyMap.UP_ARROW) {
          handleUpArrow()
          return false
        } else if (event.key === localKeyMap.ENTER) {
          if (event.ctrlKey || event.metaKey) {
            return false
          } else {
            event.preventDefault()
            event.stopPropagation()
            return handleEnter()
          }
        } else {
          // Set the highlighted option to the first matching open
          setHighlightedOption(first(items) ?? null)
          return false
        }
      },
    }))

    const preventBlur = (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }

    return (
      <Popover
        anchorEl={referenceEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        disableAutoFocus
        disableEnforceFocus
        onClick={preventBlur}
        onMouseDown={preventBlur}
        open={open}
      >
        {items.map((user) => (
          <MenuItem
            key={user.id}
            label={
              <div className="flex items-center justify-between gap-2">
                <UserAvatar small user={user} />
                {user.firstName} {user.lastName}
              </div>
            }
            onClick={() => selectUser(user)}
            selected={highlightedOption?.id === user.id}
            showCheckmark={false}
            variant={'default'}
          />
        ))}
        {items.length === 0 && (
          <MenuItem
            label={t('common.no_results_found')}
            selected={false}
            showCheckmark={false}
            variant={'default'}
          />
        )}
      </Popover>
    )
  },
)

MentionList.displayName = 'MentionList'
